import React, { memo, useId, useLayoutEffect, useMemo, useRef, useState } from "react";

const computeOverlayRect = () => {
  const sidebarEl = document.querySelector(".sidebarContainer .sidebar");
  const headerEl = document.querySelector("header");
  const contentsEl = document.querySelector(".layoutContainer .main .contents");

  const sidebarRect = sidebarEl?.getBoundingClientRect();
  const headerRect = headerEl?.getBoundingClientRect();
  const contentsRect = contentsEl?.getBoundingClientRect();

  const hasLayoutAnchors = !!(contentsRect || headerRect || sidebarRect);

  if (!hasLayoutAnchors) {
    return { top: 0, left: 0, right: 0, bottom: 0 };
  }

  const top = contentsRect?.top ?? headerRect?.bottom ?? 0;
  const left = contentsRect?.left ?? (sidebarRect?.width ?? 0);
  const right = contentsRect ? window.innerWidth - contentsRect.right : 0;
  const bottom = 0;

  return {
    top: Math.max(0, Math.round(top)),
    left: Math.max(0, Math.round(left)),
    right: Math.max(0, Math.round(right)),
    bottom,
  };
};

const LoaderComponent = memo(({ className, size = 150, padding = "p-5" }) => {
  const baseSize =
    typeof size === "number"
      ? size
      : Number.parseFloat(String(size).replace("px", "")) || 150;
  const renderedSize = Math.round(baseSize * 0.8);
  const scopeId = useId().replace(/:/g, "");

  const hostRef = useRef(null);

  const [overlayRect, setOverlayRect] = useState({
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
  });

  const [isDarkMode, setIsDarkMode] = useState(
    () => document.body.classList.contains("dark-mode")
  );

  const [isModal, setIsModal] = useState(false);

  const wrapperClassName = useMemo(() => {
    return (
      "d-flex justify-content-center align-items-center" +
      (className ? " " + className : "") +
      (padding ? " " + padding : "")
    );
  }, [className, padding]);

  const animationStyles = useMemo(
    () => `
      @keyframes ${scopeId}-pulse {
        0%, 100% { transform: scale(0.96); }
        50% { transform: scale(1.04); }
      }
      @keyframes ${scopeId}-spin-ccw {
        from { transform: rotate(0deg); }
        to { transform: rotate(-360deg); }
      }
      @keyframes ${scopeId}-spin-cw {
        from { transform: rotate(0deg); }
        to { transform: rotate(360deg); }
      }
      @keyframes ${scopeId}-bob {
        0%, 100% { transform: translateY(0); }
        25% { transform: translateY(-3px); }
        75% { transform: translateY(3px); }
      }
      @keyframes ${scopeId}-blink {
        0%, 100% { opacity: 0.2; }
        50% { opacity: 1; }
      }
      .${scopeId}-pulse-wrap {
        display: block;
        width: ${renderedSize}px;
        height: ${renderedSize}px;
        animation: ${scopeId}-pulse 1.2s ease-in-out infinite;
        transform-origin: center center;
      }
      .${scopeId}-ring-1,
      .${scopeId}-ring-2,
      .${scopeId}-ring-3 {
        transform-box: fill-box;
        transform-origin: center;
      }
      .${scopeId}-ring-1 {
        animation: ${scopeId}-spin-ccw 2.2s linear infinite;
      }
      .${scopeId}-ring-2 {
        animation: ${scopeId}-spin-cw 1.6s linear infinite;
      }
      .${scopeId}-ring-3 {
        animation: ${scopeId}-spin-ccw 1.1s linear infinite;
      }
      .${scopeId}-icon {
        animation: ${scopeId}-bob 1.8s ease-in-out infinite;
        transform-box: fill-box;
        transform-origin: center;
      }
      .${scopeId}-dot-1 {
        animation: ${scopeId}-blink 1s ease-in-out infinite;
      }
      .${scopeId}-dot-2 {
        animation: ${scopeId}-blink 1s ease-in-out 0.2s infinite;
      }
      .${scopeId}-dot-3 {
        animation: ${scopeId}-blink 1s ease-in-out 0.4s infinite;
      }
    `,
    [scopeId, renderedSize]
  );

  useLayoutEffect(() => {
    const host = hostRef.current;
    if (!host) return;

    const inModal = !!host.closest(".modal-container");
    if (inModal) {
      setIsModal(true);
      return;
    }

    const compute = () => {
      const nextDark = document.body.classList.contains("dark-mode");
      setIsDarkMode((prev) => (prev === nextDark ? prev : nextDark));

      const nextRect = computeOverlayRect();
      setOverlayRect((prev) =>
        prev.top === nextRect.top &&
        prev.left === nextRect.left &&
        prev.right === nextRect.right &&
        prev.bottom === nextRect.bottom
          ? prev
          : nextRect
      );
    };

    compute();

    window.addEventListener("resize", compute);

    const mo = new MutationObserver(() => compute());
    mo.observe(document.body, { attributes: true, attributeFilter: ["class"] });
    const sidebarContainer = document.querySelector(".sidebarContainer");
    if (sidebarContainer) {
      mo.observe(sidebarContainer, {
        attributes: true,
        attributeFilter: ["class", "style"],
      });
    }

    return () => {
      window.removeEventListener("resize", compute);
      mo.disconnect();
    };
  }, []);

  const overlayStyle =
    !isModal
      ? {
          position: "fixed",
          top: overlayRect.top,
          left: overlayRect.left,
          right: overlayRect.right,
          bottom: overlayRect.bottom,
          zIndex: 9999,
          background: isDarkMode
            ? "rgba(0, 0, 0, 0.35)"
            : "rgba(255, 255, 255, 0.6)",
          backdropFilter: "blur(2px)",
          pointerEvents: "none",
        }
      : undefined;

  return (
    <div ref={hostRef} className={wrapperClassName} style={overlayStyle}>
      <style>{animationStyles}</style>
      <div className={`${scopeId}-pulse-wrap`}>
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width={renderedSize}
          height={renderedSize}
          viewBox="0 0 140 140"
          fill="none"
          role="status"
          aria-label="Loading"
          style={{ display: "block", width: renderedSize, height: renderedSize }}
        >
          <g transform="translate(70 70)">
            <g className={`${scopeId}-ring-1`}>
              <circle
                cx="0"
                cy="0"
                r="65"
                fill="none"
                stroke="#CE20B0"
                strokeWidth="3"
                strokeLinecap="round"
                strokeDasharray="60 210"
              />
            </g>
          </g>
          <g transform="translate(70 70)">
            <g className={`${scopeId}-ring-2`}>
              <circle
                cx="0"
                cy="0"
                r="57"
                fill="none"
                stroke="#8E3AC0"
                strokeWidth="2.5"
                strokeLinecap="round"
                strokeDasharray="50 180"
              />
            </g>
          </g>
          <g transform="translate(70 70)">
            <g className={`${scopeId}-ring-3`}>
              <circle
                cx="0"
                cy="0"
                r="49"
                fill="none"
                stroke="#0290AC"
                strokeWidth="2"
                strokeLinecap="round"
                strokeDasharray="30 140"
              />
            </g>
          </g>

          <circle cx="70" cy="70" r="38" fill="#E9D4F7" />

          <g className={`${scopeId}-icon`}>
            <path
              d="M55 58
                 Q55 52 61 52
                 H79
                 Q85 52 85 58
                 V72
                 Q85 78 79 78
                 H68
                 L60 85
                 V78
                 H61
                 Q55 78 55 72
                 Z"
              fill="#7D298F"
            />
            <circle
              className={`${scopeId}-dot-1`}
              cx="64"
              cy="66"
              r="2"
              fill="#CE20B0"
            />
            <circle
              className={`${scopeId}-dot-2`}
              cx="70"
              cy="66"
              r="2"
              fill="#8E3AC0"
            />
            <circle
              className={`${scopeId}-dot-3`}
              cx="76"
              cy="66"
              r="2"
              fill="#0290AC"
            />
          </g>
        </svg>
      </div>
    </div>
  );
});

export default LoaderComponent;
